/** @jsxImportSource @emotion/react */
import { Children, isValidElement, useMemo } from 'react';
import Markdown, { type Options as MarkdownOptions } from 'react-markdown';
import remarkFlexibleMarkers from 'remark-flexible-markers';
import remarkGfm from 'remark-gfm';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ScrollableTable } from '@trinity/chat-components/components/Markdown/ScrollableTable';

type MessageResponseMarkdownProps = {
  value: string;
};

export const MessageResponseMarkdown = ({ value }: MessageResponseMarkdownProps) => {
  const components = useMarkdownComponents();

  return (
    <Markdown
      css={markdownCss}
      remarkPlugins={[remarkGfm, remarkFlexibleMarkers]}
      components={components}>
      {value}
    </Markdown>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export function useMarkdownComponents(): MarkdownOptions['components'] {
  return useMemo(() => ({
    p: props => (
      <StyledP>
        {props.children}
      </StyledP>
    ),
    a: props => {
      const kids = Children.toArray(props.children);
      // If the link wraps an img (clickable avatar), render inline
      if (kids.length === 1 && isValidElement(kids[0]) && kids[0].type === 'img') {
        return (
          <a href={props.href} target="_blank" rel="noopener" style={{ textDecoration: 'none' }}>
            {kids[0]}
          </a>
        );
      }
      // Render as button if href contains segmentation builder sessionId param
      // or if link text contains "Continue in Segmentation Builder"
      const isBuilderLink = props.href?.includes('/segmentation?sessionId=');
      const childText = getTextContent(props.children);
      const isButtonText = childText.toLowerCase().includes('continue in segmentation builder')
        || childText.toLowerCase().includes('view all segments')
        || childText.toLowerCase().includes('view cross-segment')
        || childText.toLowerCase().includes('explore segments');
      if (isBuilderLink || isButtonText) {
        return (
          <StyledButtonLink href={props.href} target="_blank" rel="noopener">
            {props.children}
          </StyledButtonLink>
        );
      }
      return (
        <StyledA
          href={props.href}
          target="_blank"
          rel="noopener">
          {props.children}
        </StyledA>
      );
    },
    img: props => {
      const isAvatar = props.src?.includes('avatar-presets');
      if (isAvatar) {
        return (
          <StyledAvatarImg
            src={props.src}
            alt={props.alt || ''}
          />
        );
      }
      return <img src={props.src} alt={props.alt || ''} style={{ maxWidth: '100%' }} />;
    },
    ul: props => (
      <StyledUl>
        {props.children}
      </StyledUl>
    ),
    li: props => (
      <StyledLi>
        {props.children}
      </StyledLi>
    ),
    ol: props => (
      <StyledOL>
        {props.children}
      </StyledOL>
    ),
    strong: props => {
      const kids = Children.toArray(props.children);
      if (kids.length === 1 && isValidElement(kids[0]) && kids[0].type === 'a') {
        const link = kids[0] as React.ReactElement<{ href?: string; children?: React.ReactNode }>;
        return (
          <StyledButtonLink
            href={link.props.href}
            target="_blank"
            rel="noopener">
            {link.props.children}
          </StyledButtonLink>
        );
      }
      return (
        <StyledStrong>
          {props.children}
        </StyledStrong>
      );
    },
    h1: props => (
      <StyledH1>
        {props.children}
      </StyledH1>
    ),
    h2: props => (
      <StyledH2>
        {props.children}
      </StyledH2>
    ),
    h3: props => (
      <StyledH3>
        {props.children}
      </StyledH3>
    ),
    table: props => {
      return (
        <ScrollableTable>
          {props.children}
        </ScrollableTable>
      );
    },
    tr: props => (
      <StyledTr>
        {props.children}
      </StyledTr>
    ),
    td: props => (
      <StyledTd>
        {props.children}
      </StyledTd>
    ),
    th: props => (
      <StyledTh>
        {props.children}
      </StyledTh>
    ),
    mark: props => (
      <StyledMark>
        {props.children}
      </StyledMark>
    ),
  }), []);
}

function getTextContent(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getTextContent).join('');
  if (isValidElement(node)) {
    const p = node.props as Record<string, unknown>;
    if (p.children) return getTextContent(p.children as React.ReactNode);
  }
  return '';
}

const markdownCss = css({
  whiteSpace: 'normal',
});

const StyledMark = styled.mark({
  backgroundColor: 'yellow',
  borderRadius: 3,
});

const StyledStrong = styled.strong(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
}));

const StyledAvatarImg = styled.img({
  width: 40,
  height: 40,
  borderRadius: '50%',
  objectFit: 'cover',
  verticalAlign: 'middle',
  marginRight: 8,
  border: '2px solid #e0e0e0',
});

const StyledButtonLink = styled.a(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 16px',
  borderRadius: 8,
  border: '1px solid ' + (theme.palette.primary.main),
  background: 'transparent',
  color: theme.palette.primary.main,
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'all 0.15s',
  marginTop: 4,
  marginBottom: 4,
  '&:hover': {
    background: theme.palette.primary.main,
    color: '#fff',
  },
}));

const StyledP = styled.p({
  margin: 0,
});

const StyledOL = styled.ol({
  paddingInlineStart: 16,
});

const StyledA = styled.a(({ theme }) => ({
  cursor: 'pointer',
  textDecoration: 'underline',
  fontSize: 16,

  ':hover': {
    color: theme.palette.primary.main,
  },
}));

const StyledUl = styled.ul({
  paddingInlineStart: 16,
});

const StyledLi = styled.li(({ theme }) => ({
  marginTop: 8,
  marginBottom: 8,

  '&::last-child': {
    marginBottom: 0,
  },

  '::marker': {
    fontFamily: theme.fonts.semiBold,
  },
}));

const StyledH1 = styled.h1(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 18,
  marginTop: 22,
  marginBottom: 10,

  '&:first-child': {
    marginTop: 0,
  },
}));

const StyledH2 = styled.h2(({ theme }) => ({
  fontFamily: theme.fonts.bold,
  fontSize: 16,
  marginTop: 22,
  marginBottom: 10,

  '&:first-child': {
    marginTop: 0,
  },
}));

const StyledH3 = styled.h3(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 16,
  marginTop: 18,
  marginBottom: 6,

  '&:first-child': {
    marginTop: 0,
  },
}));

const StyledTr = styled.tr({
  backgroundColor: '#ffffff',
  borderTop: `1px solid hsla(210, 18%, 87%, 1)`,

  ':nth-child(2n)': {
    backgroundColor: '#f6f8fa',
  },
});

const StyledTd = styled.td({
  padding: '6px 13px',
  border: `1px solid #d0d7de`,
});

const StyledTh = styled.th(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  padding: '6px 13px',
  border: `1px solid #d0d7de`,
}));